import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "@redux/hooks";
import {
  activateAccount,
  resetActivateAccount,
} from "@user-management-context/write/application/use-cases/account-activation/activate-account";
import { selectAccountActivation } from "@user-management-context/write/application/use-cases/account-activation/selectors/account-activation-selectors";
import { useApiAction } from "@shared-kernel/primary/shared/api-errors/use-api-action";
import { applyApiFieldErrors } from "@shared-kernel/primary/shared/api-errors/apply-api-field-errors";
import type { ActionForm } from "@shared-kernel/primary/shared/api-errors/action-form";
import { useResetWhenSettled } from "@shared-kernel/primary/shared/request-state/use-reset-when-settled";
import { accountActivationFormatFormDataToBodyData, type AccountActivationFormInputs } from "./form-validation/account-activation.schema";

export const useAccountActivation = (form: ActionForm<AccountActivationFormInputs>, userId: string, token: string) => {
  const navigate = useNavigate();
  const run = useApiAction();
  const { processing } = useAppSelector(selectAccountActivation);
  useResetWhenSettled(processing, resetActivateAccount);

  // L'API dit pourquoi l'activation est refusée (compte déjà activé, lien expiré ou remplacé), sous le champ désigné
  const activate = useCallback(
    async (values: AccountActivationFormInputs) => {
      const outcome = await run(activateAccount(accountActivationFormatFormDataToBodyData(values, userId, token)), {
        success: "Votre compte a été activé !",
        failure: "Erreur lors de l'activation de votre compte",
      });
      if (!outcome.ok) {
        applyApiFieldErrors(form, outcome.error);
        return;
      }
      navigate("/login");
    },
    [run, navigate, form, userId, token],
  );

  return { activate };
};
